import { createFileRoute } from "@tanstack/react-router";
import { trackPhoneClick } from "@/lib/track-phone-click";
import { trackEmailClick } from "@/lib/track-email-click";
import { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import heroImg from "@/assets/ib-hero.webp";
import useCaseImg from "@/assets/ib-usecase.webp";
import finishImg from "@/assets/ib-finish.webp";
import anwendungImg from "@/assets/ib-anwendung.webp";
import detailImg from "@/assets/ib-detail.webp";
import ableitImg from "@/assets/ib-ableit.webp";
import teamImg from "@/assets/ib-team.jpg";
import logoImg from "@/assets/logo.png";
import { AttributionFields } from "@/components/AttributionFields";
import { HeroLeadForm } from "@/components/HeroLeadForm";

export const Route = createFileRoute("/industriebodenbeschichtung-v2")({
  head: () => ({
    meta: [
      {
        title:
          "Industriebodenbeschichtung Gewerbe & Industrie | Schneider Bodenbeschichtungen GmbH",
      },
      {
        name: "description",
        content:
          "Industriebodenbeschichtung für Werkstätten, Lagerhallen, Produktion und WHG-/ESD-Bereiche. Familiengeführter Fachbetrieb nach WHG mit über 20 Jahren Erfahrung in NRW. Projekt unverbindlich anfragen.",
      },
      {
        property: "og:title",
        content:
          "Industriebodenbeschichtung für Gewerbe und Industrie – Schneider",
      },
      {
        property: "og:description",
        content:
          "Robuste, fachgerechte Bodenbeschichtungen für gewerblich und industriell genutzte Flächen. WHG-Fachbetrieb, präqualifiziert. Über 20 Jahre Erfahrung.",
      },
      { property: "og:type", content: "website" },
      { property: "og:image", content: heroImg },
      { property: "twitter:image", content: heroImg },
      { name: "robots", content: "index,follow" },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://lp.schneider-bodenbeschichtungen.de/industriebodenbeschichtung",
      },
    ],
  }),
  component: LandingV2,
});

const PHONE_TEL = "+492841931010";
const PHONE_DISPLAY = "+49 2841 931010";
const EMAIL = "info@schneider-bbs.de";
const FORM_ENDPOINT = "";

function scrollToForm() {
  document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" });
}

/* ─── Header ─── */
function Header() {
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur-sm">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
        <a href="#hero" className="inline-flex items-center">
          <img
            src={logoImg}
            alt="Schneider Bodenbeschichtungen GmbH"
            className="h-9 w-auto md:h-10"
          />
        </a>
        <div className="flex items-center gap-2">
          <a
            href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("industrieboden_v2", PHONE_TEL)}
            className="hidden h-10 items-center gap-2 rounded-md border border-border px-3 text-sm font-medium text-foreground transition-colors hover:border-foreground sm:inline-flex"
          >
            <svg viewBox="0 0 20 20" className="h-4 w-4 fill-current" aria-hidden="true">
              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
            </svg>
            {PHONE_DISPLAY}
          </a>
          <button
            type="button"
            onClick={scrollToForm}
            className="hidden h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 md:inline-flex"
          >
            Projekt anfragen
          </button>
        </div>
      </div>
    </header>
  );
}

/* ─── Sticky Mobile CTA ─── */
function StickyMobileCTA() {
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 p-2 shadow-[0_-4px_16px_-4px_rgba(0,0,0,0.08)] backdrop-blur-sm md:hidden">
      <div className="flex gap-2">
        <a
          href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("industrieboden_v2", PHONE_TEL)}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-foreground px-3 py-3 text-sm font-semibold text-foreground"
        >
          Anrufen
        </a>
        <button
          type="button"
          onClick={scrollToForm}
          className="flex flex-[1.4] items-center justify-center rounded-md bg-primary px-3 py-3 text-sm font-semibold text-primary-foreground shadow-sm"
        >
          Projekt anfragen
        </button>
      </div>
    </div>
  );
}

/* ─── Section wrapper ─── */
function Section({
  children,
  className = "",
  id,
  gray = false,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
  gray?: boolean;
}) {
  return (
    <section
      id={id}
      className={`scroll-mt-24 ${gray ? "bg-muted/40" : "bg-background"} border-b border-border ${className}`}
    >
      <div className="mx-auto max-w-6xl px-4 py-12 md:py-20">{children}</div>
    </section>
  );
}

/* ─── Icons ─── */
function Star() {
  return (
    <svg viewBox="0 0 20 20" className="h-4 w-4 fill-current" aria-hidden="true">
      <path d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.79L10 14.77l-5.2 2.74.99-5.79L1.58 7.62l5.82-.85L10 1.5z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 10.5l3.5 3.5 7-8" />
    </svg>
  );
}

function MinusIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 10h10" />
    </svg>
  );
}

/* ─── Hero ─── */
function Hero() {
  return (
    <section
      id="hero"
      className="scroll-mt-24 relative overflow-hidden border-b border-border bg-foreground"
    >
      <img
        src={heroImg}
        alt="Großflächige Gewerbehalle mit hochwertiger Industriebodenbeschichtung"
        className="absolute inset-0 h-full w-full object-cover opacity-50"
        fetchPriority="high"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-foreground/85 via-foreground/55 to-foreground/10" />

      <div className="relative mx-auto max-w-6xl px-4 py-14 md:py-20 lg:py-24">
        <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="min-w-0 lg:col-span-7">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-background/30 bg-background/10 px-3 py-1 text-xs font-medium uppercase tracking-wide text-background backdrop-blur">
            Für Gewerbe, Industrie & Bestandsobjekte
          </div>
          <h1
            className="text-[2rem] font-semibold leading-[1.05] tracking-tight text-background sm:text-4xl md:text-5xl lg:text-[3.25rem]"
            lang="de"
          >
            Industrieboden&shy;beschichtung, die der Belastung im Betrieb standhält
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-background/90 md:text-lg">
            Belastbare Systeme für Werkstatt, Halle und Produktion – passend
            zur tatsächlichen Nutzung statt Standard von der Stange.
          </p>

          <ul className="mt-6 space-y-2.5">
            <li className="flex items-start gap-2.5 text-sm text-background md:text-base">
              <CheckIcon />
              <span>System wird auf Untergrund, Last und Nutzung abgestimmt</span>
            </li>
            <li className="flex items-start gap-2.5 text-sm text-background md:text-base">
              <CheckIcon />
              <span>Auch Beschichtung im Bestand sowie WHG- und ableitfähige Aufbauten</span>
            </li>
            <li className="flex items-start gap-2.5 text-sm text-background md:text-base">
              <CheckIcon />
              <span>Ausschließlich für gewerbliche, industrielle und institutionelle Projekte</span>
            </li>
          </ul>

          <div className="mt-8">
            <a
              href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("industrieboden_v2", PHONE_TEL)}
              className="inline-flex h-12 items-center justify-center rounded-md border border-background/40 bg-transparent px-6 text-sm font-semibold text-background transition-colors hover:bg-background/10"
            >
              Jetzt anrufen · {PHONE_DISPLAY}
            </a>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-background/85">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-flex text-primary">
                <Star />
              </span>
              <strong className="text-background">4,6</strong> Google
              <span className="text-background/60">(14 Rezensionen)</span>
            </span>
            <span className="hidden text-background/40 md:inline">•</span>
            <span>Fachbetrieb nach WHG</span>
            <span className="hidden text-background/40 md:inline">•</span>
            <span>Präqualifiziert (PQ)</span>
            <span className="hidden text-background/40 md:inline">•</span>
            <span>20+ Jahre · familiengeführt</span>
          </div>
          </div>

          <div className="lg:col-span-5">
            <HeroLeadForm
              pageId="industrieboden_v2"
              formName="Industrieboden V2 Hero Formular"
              variant="industrie"
              phoneTel={PHONE_TEL}
              phoneDisplay={PHONE_DISPLAY}
              topicOptions={[
                "Industriebodenbeschichtung",
                "Bodenbeschichtung im Bestand",
                "WHG-Bereich",
                "ESD / ableitfähig",
                "Werkstatt / Lager / Produktion",
                "Sonstiges gewerbliches Projekt",
              ]}
            />
            <p className="mt-4 text-center text-xs leading-relaxed text-background/75 lg:text-left">
              Nicht sicher, ob Ihr Boden für eine neue Beschichtung geeignet
              ist? Genau dafür ist die erste Einschätzung da.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Trust bar ─── */
function TrustBar() {
  const items = [
    { label: "4,6 ★ Google", sub: "14 Rezensionen", strong: true, star: true },
    { label: "20+ Jahre", sub: "Erfahrung" },
    { label: "Fachbetrieb", sub: "nach WHG" },
    { label: "Präqualifiziert", sub: "(PQ)" },
    { label: "Familiengeführt", sub: "Fachbetrieb" },
  ];
  return (
    <div className="border-b border-border bg-background">
      <div className="mx-auto grid max-w-6xl grid-cols-2 gap-x-4 gap-y-4 px-4 py-6 sm:grid-cols-3 md:flex md:items-center md:justify-between md:gap-6 md:py-7">
        {items.map((it) => (
          <div key={it.label} className="flex flex-col text-sm leading-tight">
            <span
              className={`inline-flex items-center gap-1 ${it.strong ? "font-semibold text-foreground" : "font-medium text-foreground"}`}
            >
              {it.star && (
                <span className="text-primary">
                  <Star />
                </span>
              )}
              {it.label}
            </span>
            <span className="text-xs text-muted-foreground">{it.sub}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Wann Schneider die richtige Wahl ist (Audience qualifier) ─── */
function AudienceFit() {
  const idealFor = [
    "Gewerblich genutzte Werkstätten und Hallen",
    "Industrie-, Produktions- und Lagerflächen",
    "Abgenutzte Bestandsflächen",
    "WHG-, ESD- oder Sonderanforderungen",
  ];
  const notFor = [
    "Private Garagen und Hobbywerkstätten",
    "DIY- oder Kleinstprojekte",
  ];
  return (
    <Section id="passend" className="!py-10 md:!py-14">
      <div className="mb-6 max-w-3xl">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-muted/60 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Wann Schneider die richtige Wahl ist
        </div>
        <h2 className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          Geeignet für gewerbliche und industrielle Flächen – nicht für private Projekte.
        </h2>
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        <div className="rounded-xl border border-border bg-background p-6 shadow-sm">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary">
            Ideal für
          </div>
          <ul className="mt-3 space-y-2.5">
            {idealFor.map((i) => (
              <li
                key={i}
                className="flex items-start gap-2.5 text-sm text-foreground"
              >
                <CheckIcon />
                <span>{i}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-border bg-background p-6 shadow-sm">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Nicht geeignet für
          </div>
          <ul className="mt-3 space-y-2.5">
            {notFor.map((i) => (
              <li
                key={i}
                className="flex items-start gap-2.5 text-sm text-muted-foreground"
              >
                <MinusIcon />
                <span>{i}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
            Fokus liegt auf gewerblichen, industriellen und institutionellen
            Projekten.
          </p>
        </div>
      </div>
    </Section>
  );
}

/* ─── Pain points ─── */
function PainPoints() {
  const items = [
    {
      problem: "Rissige, staubende oder abgenutzte Böden",
      outcome:
        "Belastbare Flächen, die im Werkstatt- und Hallenbetrieb wieder verlässlich nutzbar sind.",
    },
    {
      problem: "Hohe Belastung in Werkstatt, Halle und Produktion",
      outcome:
        "Systeme passend zu Stapler-, Maschinen- und Mitarbeiterverkehr – statt Standard von der Stange.",
    },
    {
      problem: "Beschichtungsarbeiten im laufenden Betrieb",
      outcome:
        "Realistische Planung und abgestimmte Umsetzung, die Ausfallzeiten gering hält.",
    },
    {
      problem: "WHG-, ESD- oder Sonderanforderungen",
      outcome:
        "Fachgerechte Systemwahl als Fachbetrieb nach WHG – auch in regulierten Bereichen.",
    },
  ];
  return (
    <Section id="probleme" gray>
      <div className="mb-10 max-w-3xl">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Typische Probleme im Betrieb
        </div>
        <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          Wo Industrieböden im Alltag scheitern
        </h2>
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        {items.map((it) => (
          <div
            key={it.problem}
            className="flex h-full flex-col rounded-xl border border-border bg-background p-6 shadow-sm"
          >
            <h3 className="text-base font-semibold tracking-tight text-foreground">
              {it.problem}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {it.outcome}
            </p>
          </div>
        ))}
      </div>
      <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <button
          type="button"
          onClick={scrollToForm}
          className="inline-flex h-12 items-center justify-center rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-md shadow-primary/20 transition-colors hover:bg-primary/90"
        >
          Projekt anfragen
        </button>
        <a
          href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("industrieboden_v2", PHONE_TEL)}
          className="inline-flex h-12 items-center justify-center rounded-md border border-border bg-background px-6 text-sm font-medium text-foreground transition-colors hover:border-foreground"
        >
          {PHONE_DISPLAY}
        </a>
      </div>
    </Section>
  );
}

/* ─── Project proof ─── */
function ProjectProof() {
  const cards = [
    {
      img: finishImg,
      title: "Großflächige Industriehalle",
      text: "Belastbare, gleichmäßige Flächenlösung für gewerblich genutzte Hallen.",
    },
    {
      img: ableitImg,
      title: "Ableitfähige Beschichtung",
      text: "Funktionale Systeme, abgestimmt auf Untergrund, Nutzung und Aufbau.",
    },
    {
      img: anwendungImg,
      title: "Ausführung im laufenden Projekt",
      text: "Saubere Vorbereitung, gewissenhafte Verarbeitung, kontrollierte Umsetzung.",
    },
    {
      img: detailImg,
      title: "Detail aus der Verarbeitung",
      text: "Handwerkliche Qualität im Umgang mit anspruchsvollen Bestandsflächen.",
    },
  ];
  return (
    <Section id="referenzen">
      <div className="mb-10 max-w-2xl">
        <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          Einblicke aus realisierten Projekten
        </h2>
        <p className="mt-3 text-base text-muted-foreground">
          Reale Projektbilder aus gewerblich und industriell genutzten Flächen.
        </p>
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        {cards.map((c, i) => (
          <article
            key={c.title}
            className="overflow-hidden rounded-xl border border-border bg-background shadow-sm"
          >
            <div className="aspect-[16/10] overflow-hidden bg-muted">
              <img
                src={c.img}
                alt={c.title}
                loading={i === 0 ? "eager" : "lazy"}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="p-5">
              <h3 className="text-base font-semibold tracking-tight text-foreground">
                {c.title}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {c.text}
              </p>
            </div>
          </article>
        ))}
      </div>
    </Section>
  );
}

/* ─── Use cases ─── */
function UseCases() {
  const cases = [
    "Werkstätten",
    "Lagerhallen",
    "Produktionsflächen",
    "Technikräume",
    "Bestandsflächen",
    "WHG / ableitfähige Systeme",
  ];

  return (
    <Section id="leistungen" gray>
      <div className="grid gap-10 md:grid-cols-12 md:items-start md:gap-12">
        <div className="md:col-span-7">
          <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
            Lösungen für stark beanspruchte gewerbliche Flächen
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Es kommt nicht auf irgendeine Beschichtung an, sondern auf ein
            System, das zu Untergrund, Nutzung und realer Belastung passt.
          </p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {cases.map((c) => (
              <li
                key={c}
                className="flex items-center gap-2.5 rounded-md border border-border bg-background px-4 py-3 text-sm font-medium text-foreground"
              >
                <CheckIcon />
                {c}
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-5">
          <div className="overflow-hidden rounded-xl border border-border bg-muted shadow-sm">
            <img
              src={useCaseImg}
              alt="Werkstatt mit professioneller Bodenbeschichtung in Gewerbeumgebung"
              loading="lazy"
              className="aspect-[4/3] h-full w-full object-cover"
            />
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ─── Why Schneider + Authority (merged) ─── */
function WhyAndAuthority() {
  const cards = [
    {
      title: "Ehrliche Beratung",
      text: "Empfohlen wird, was zum Objekt, zur Nutzung und zum Untergrund passt – nicht das Maximalpaket.",
    },
    {
      title: "Passende Systemwahl",
      text: "Werkstatt, Halle, Produktion oder Sonderbereich – die Beschichtung passt zur realen Beanspruchung.",
    },
    {
      title: "Saubere Ausführung",
      text: "Terminverbindlichkeit, gründliche Vorbereitung und fachgerechte Umsetzung sind Standard.",
    },
    {
      title: "Anspruchsvolle Anforderungen",
      text: "Erfahrung mit Beschichtung im Bestand, WHG-orientierten Aufbauten und ableitfähigen Systemen.",
    },
  ];
  return (
    <Section id="warum-schneider">
      <span id="technik" className="block -translate-y-24" aria-hidden="true" />
      <span id="erfahrung-regionalitaet" className="block -translate-y-24" aria-hidden="true" />
      <div className="grid gap-10 md:grid-cols-12 md:items-start md:gap-14">
        <div className="md:col-span-5">
          <div className="overflow-hidden rounded-xl border border-border bg-muted shadow-sm">
            <img
              src={teamImg}
              alt="Maurice Schneider und Andreas Schneider, Geschäftsführung Schneider Bodenbeschichtungen GmbH"
              loading="lazy"
              width={1024}
              height={1024}
              className="aspect-[4/5] h-full w-full object-cover"
            />
          </div>
          <div className="mt-3 text-xs font-medium text-muted-foreground">
            Maurice Schneider &amp; Andreas Schneider
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3">
            {[
              { v: "20+", l: "Jahre Erfahrung" },
              { v: "2", l: "Standorte in NRW" },
              { v: "WHG", l: "Fachbetrieb" },
              { v: "PQ", l: "Präqualifiziert" },
            ].map((s) => (
              <div
                key={s.l}
                className="rounded-lg border border-border bg-background p-4"
              >
                <div className="text-xl font-semibold text-foreground">
                  {s.v}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-7">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-muted/60 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Warum Schneider · Erfahrung · Region
          </div>
          <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
            Familiengeführter Fachbetrieb mit über 20 Jahren Erfahrung
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Mit Standorten in Moers und Wuppertal betreuen wir Industrieboden-
            und Beschichtungsprojekte in ganz Nordrhein-Westfalen – vom
            Niederrhein über das Ruhrgebiet bis in den Großraum Köln.
          </p>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {cards.map((c, i) => (
              <div
                key={c.title}
                className="rounded-xl border border-border bg-background p-5 shadow-sm"
              >
                <div className="mb-2 inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-xs font-semibold text-primary">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="text-base font-semibold tracking-tight text-foreground">
                  {c.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                  {c.text}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-lg border border-border bg-muted/40 p-5">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Technische Tiefe
            </div>
            <ul className="mt-2 space-y-2 text-sm text-foreground">
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>
                  <strong>WHG- und Sonderanforderungen:</strong> Systeme passend zu
                  belasteten Untergründen und regulierten Bereichen.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>
                  <strong>Beschichtung im Bestand:</strong> nach Prüfung von Untergrund
                  und Zustand – technisch und wirtschaftlich abgestimmt.
                </span>
              </li>
            </ul>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-border bg-background p-3 text-xs">
              <div className="font-semibold text-foreground">Moers</div>
              <div className="mt-0.5 text-muted-foreground">
                Eurotec-Ring 36 · 47445 Moers
              </div>
            </div>
            <div className="rounded-md border border-border bg-background p-3 text-xs">
              <div className="font-semibold text-foreground">Wuppertal</div>
              <div className="mt-0.5 text-muted-foreground">
                Kreuzstraße 20 · 42277 Wuppertal
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ─── Reviews ─── */
function Reviews() {
  const reviews = [
    {
      text: "Herr Schneider berät ehrlich und nicht nur, um zu verkaufen. Die Beratung war passend zum Objekt und mir wurde eine andere Lösung empfohlen.",
      author: "Hannah Luisa Vimpolsek",
    },
    {
      text: "Einwandfreie und termingerechte Ausführung.",
      author: "Florian Heil",
    },
    {
      text: "Der Service ist einwandfrei und professionell.",
      author: "Sergio B.",
    },
  ];
  return (
    <Section gray>
      <div className="mb-8 max-w-2xl">
        <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          Was Kunden sagen
        </h2>
        <p className="mt-3 text-sm text-muted-foreground">
          Auszug aus 14 Google-Rezensionen · Gesamtbewertung 4,6 ★
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {reviews.map((r) => (
          <figure
            key={r.author}
            className="flex flex-col rounded-xl border border-border bg-background p-5 shadow-sm"
          >
            <div className="mb-2.5 flex items-center gap-1 text-primary">
              <Star />
              <Star />
              <Star />
              <Star />
              <Star />
            </div>
            <blockquote className="flex-1 text-sm leading-relaxed text-foreground">
              „{r.text}"
            </blockquote>
            <figcaption className="mt-3 border-t border-border pt-2.5 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{r.author}</span>
              <span className="block">Google-Rezension</span>
            </figcaption>
          </figure>
        ))}
      </div>
    </Section>
  );
}

/* ─── FAQ ─── */
function FAQ() {
  const items = [
    {
      q: "Für welche Flächen eignet sich eine Industriebodenbeschichtung?",
      a: "Geeignet sind unter anderem Werkstätten, Lagerhallen, Produktionsflächen, Technikräume und andere gewerblich stark beanspruchte Bereiche.",
    },
    {
      q: "Lassen sich bestehende Industrieböden neu beschichten?",
      a: "Ja. Häufig geht es darum, vorhandene Flächen technisch zu prüfen und mit einem passenden Beschichtungssystem für die weitere Nutzung vorzubereiten.",
    },
    {
      q: "Ist Beschichtungsarbeiten im laufenden Betrieb möglich?",
      a: "In vielen Fällen ja – mit realistischer Planung und abgestimmter Umsetzung lassen sich Stillstand und Ausfallzeiten gering halten.",
    },
    {
      q: "Sind WHG- oder ableitfähige Systeme möglich?",
      a: "Ja. In bestimmten gewerblichen und industriellen Einsatzbereichen können WHG-orientierte oder ableitfähige Systeme erforderlich sein – als Fachbetrieb nach WHG planen wir diese fachgerecht ein.",
    },
    {
      q: "Wie läuft eine Erstprüfung ab?",
      a: "Nach Eingang der Anfrage melden wir uns mit einer ersten Einschätzung. Bei passenden Projekten folgt eine Bestandsaufnahme vor Ort und ein abgestimmtes Angebot.",
    },
    {
      q: "Werden auch private Projekte angeboten?",
      a: "Nein. Der Fokus liegt ausschließlich auf gewerblichen, industriellen und institutionellen Projekten.",
    },
  ];
  return (
    <Section id="faq">
      <div className="mb-10 max-w-2xl">
        <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          Häufige Fragen
        </h2>
      </div>
      <Accordion type="single" collapsible className="mx-auto max-w-3xl">
        {items.map((it, i) => (
          <AccordionItem
            key={i}
            value={`item-${i}`}
            className="border-b border-border"
          >
            <AccordionTrigger className="py-5 text-left text-base font-semibold text-foreground hover:no-underline">
              {it.q}
            </AccordionTrigger>
            <AccordionContent className="pb-5 text-sm leading-relaxed text-muted-foreground">
              {it.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Section>
  );
}

/* ─── Contact form ─── */
function ContactForm() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    e.stopPropagation();
    setStatus("sending");
    const formEl = e.currentTarget;
    const fd = new FormData(formEl);
    const payload = Object.fromEntries(fd.entries()) as Record<string, string>;
    try {
      const { submitLead } = await import("@/lib/submit-lead");
      await submitLead(
        {
          name: payload.name || "",
          firma: payload.firma || "",
          email: payload.email || "",
          telefon: payload.telefon || "",
          ort: payload.ort || "",
          betreff: payload.betreff || "",
          nachricht: payload.nachricht || "",
        },
        "industrieboden_v2",
        payload.website || "",
        formEl
      );
      if (typeof window !== "undefined") {
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({
          event: "generate_lead",
          page_id: "industrieboden_v2",
        });
      }
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  const inputClass =
    "w-full rounded-md border border-border bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground";
  const labelClass = "mb-1.5 block text-xs font-medium text-foreground";

  return (
    <Section id="kontakt" gray>
      <span id="projektanfrage" className="block -translate-y-24" aria-hidden="true" />
      <div className="grid gap-10 md:grid-cols-12 md:gap-12">
        <div className="md:col-span-5">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Projektanfrage
          </div>
          <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
            Erste Einschätzung für Ihre Industriebodenbeschichtung anfragen
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Senden Sie uns kurz die wichtigsten Eckdaten zu Objekt, Fläche und
            Zustand des Bodens. Wir prüfen Ihre Anfrage und melden uns in der
            Regel innerhalb von 24 Stunden mit einer ersten Einschätzung.
          </p>

          <div className="mt-5 flex flex-wrap gap-2">
            {[
              "Unverbindlich",
              "Für gewerbliche / industrielle Objekte",
              "Antwort in 24 Stunden",
            ].map((p) => (
              <span
                key={p}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-foreground"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                {p}
              </span>
            ))}
          </div>

          <p className="mt-5 rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm leading-relaxed text-foreground">
            Nicht sicher, ob Ihr Boden für eine neue Beschichtung geeignet ist?
            Genau dafür ist die erste Einschätzung da.
          </p>

          {/* Was nach dem Absenden passiert */}
          <div className="mt-6 rounded-xl border border-border bg-background p-5 shadow-sm">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Was nach dem Absenden passiert
            </div>
            <ol className="mt-3 space-y-2.5 text-sm text-foreground">
              <li className="flex gap-3">
                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
                  1
                </span>
                <span>Schnelle Sichtung der Anfrage und Rückmeldung in 24 Stunden.</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
                  2
                </span>
                <span>Bei passenden Projekten: Bestandsaufnahme vor Ort und Klärung der technischen Anforderungen.</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
                  3
                </span>
                <span>Abgestimmtes Angebot mit Systemvorschlag und realistischer Planung.</span>
              </li>
            </ol>
          </div>

          <div className="mt-8 space-y-5 text-sm text-foreground">
            <div className="rounded-lg border border-border bg-background p-5">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Lieber telefonisch?
              </div>
              <a
                href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("industrieboden_v2", PHONE_TEL)}
                className="mt-1.5 inline-flex items-center gap-2 text-xl font-semibold tracking-tight text-foreground hover:text-primary"
              >
                <svg
                  viewBox="0 0 20 20"
                  className="h-5 w-5 fill-current text-primary"
                  aria-hidden="true"
                >
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                {PHONE_DISPLAY}
              </a>
              <p className="mt-2 text-xs text-muted-foreground">
                Direkter Kontakt für gewerbliche und industrielle Projekte.
              </p>
            </div>

            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                E-Mail
              </div>
              <a
                href={`mailto:${EMAIL}`}
                onClick={() => trackEmailClick("industrieboden_v2", EMAIL)}
                className="mt-1 inline-block text-sm text-foreground underline-offset-4 hover:text-primary hover:underline"
              >
                {EMAIL}
              </a>
            </div>
          </div>
        </div>

        <div className="md:col-span-7">
          {status === "success" ? (
            <div
              role="status"
              aria-live="polite"
              className="rounded-xl border border-primary/30 bg-primary/5 p-6 shadow-sm md:p-8"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12l5 5L20 7" />
                </svg>
              </div>
              <h3 className="mt-5 text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
                Vielen Dank für Ihre Anfrage
              </h3>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                Wir haben Ihre Nachricht erhalten und melden uns zeitnah bei Ihnen zurück.
              </p>
              <p className="mt-4 text-sm text-foreground">
                Sie erreichen uns auch direkt unter{" "}
                <a
                  href={`tel:${PHONE_TEL}`}
                  onClick={() => trackPhoneClick("industrieboden_v2", PHONE_TEL)}
                  className="font-semibold text-primary hover:underline"
                >
                  {PHONE_DISPLAY}
                </a>
                .
              </p>
            </div>
          ) : (
          <div className="relative">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -inset-2 rounded-2xl bg-primary/10 blur-2xl"
            />
            <form
              onSubmit={onSubmit}
              id="industrieboden_v2_kontakt"
              data-gj-no-native-submit="1"
              data-form-id="industrieboden_v2_kontakt"
              data-form-name="Industrieboden V2 Abschluss-Formular"
              className="relative rounded-2xl border-2 border-primary bg-background p-6 shadow-2xl shadow-primary/30 md:p-8"
            >
              <AttributionFields />
              <div className="-mt-10 mb-5 flex justify-center md:justify-start">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary-foreground shadow-md">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
                  Kostenlose Erstberatung
                </span>
              </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className={labelClass}>
                  Ihr Name *
                </label>
                <input id="name" name="name" required className={inputClass} />
              </div>
              <div>
                <label htmlFor="firma" className={labelClass}>
                  Firma *
                </label>
                <input id="firma" name="firma" required className={inputClass} />
              </div>
              <div>
                <label htmlFor="email" className={labelClass}>
                  Ihre E-Mail-Adresse *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="telefon" className={labelClass}>
                  Telefonnummer *
                </label>
                <input
                  id="telefon"
                  name="telefon"
                  type="tel"
                  required
                  className={inputClass}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="ort" className={labelClass}>
                  Ort / Projektstandort
                </label>
                <input id="ort" name="ort" className={inputClass} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="betreff" className={labelClass}>
                  Worum geht es? *
                </label>
                <select
                  id="betreff"
                  name="betreff"
                  required
                  defaultValue=""
                  className={inputClass}
                >
                  <option value="" disabled>
                    Bitte wählen
                  </option>
                  <option>Industriebodenbeschichtung</option>
                  <option>Bodenbeschichtung im Bestand</option>
                  <option>WHG-Bereich</option>
                  <option>ESD / ableitfähig</option>
                  <option>Werkstatt / Lager / Produktion</option>
                  <option>Sonstiges gewerbliches Projekt</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="nachricht" className={labelClass}>
                  Kurze Beschreibung *
                </label>
                <textarea
                  id="nachricht"
                  name="nachricht"
                  required
                  rows={5}
                  placeholder="z. B. Produktionshalle, Lagerfläche oder Werkstattboden, ca. 500 m², Boden abgenutzt, staubt oder soll neu beschichtet werden."
                  className={inputClass}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={status === "sending"}
              className="mt-6 inline-flex h-12 w-full items-center justify-center rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-md transition-all hover:bg-primary/90 hover:shadow-lg disabled:opacity-60"
            >
              {status === "sending"
                ? "Wird gesendet…"
                : "Erste Einschätzung anfragen"}
            </button>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
              Nach dem Absenden prüfen wir Ihre Angaben. Wenn das Projekt passt,
              melden wir uns zur ersten Einschätzung oder zur Abstimmung der
              nächsten Schritte.
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              Pflichtfelder mit *. Nur für gewerbliche und industrielle
              Anfragen. Ihre Daten werden ausschließlich zur Bearbeitung Ihrer
              Anfrage genutzt.
            </p>

            {status === "error" && (
              <p className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                Beim Senden ist etwas schiefgegangen. Bitte versuchen Sie es
                erneut oder rufen Sie uns an.
              </p>
            )}
            </form>
          </div>
          )}
        </div>
      </div>
    </Section>
  );
}

/* ─── Footer ─── */
function Footer() {
  return (
    <footer className="bg-background pb-24 pt-10 md:pb-10">
      <div className="mx-auto max-w-6xl px-4">
        <div className="grid gap-6 border-t border-border pt-8 md:grid-cols-12 md:gap-8">
          <div className="md:col-span-5 text-xs text-muted-foreground">
            <div className="font-semibold text-foreground">
              Schneider Bodenbeschichtungen GmbH
            </div>
            <div className="mt-1">Eurotec-Ring 36 · 47445 Moers</div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              <a href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("industrieboden_v2", PHONE_TEL)} className="hover:text-foreground">
                {PHONE_DISPLAY}
              </a>
              <a href={`mailto:${EMAIL}`} onClick={() => trackEmailClick("industrieboden_v2", EMAIL)} className="hover:text-foreground">
                {EMAIL}
              </a>
            </div>
          </div>
          <div className="md:col-span-4 text-xs text-muted-foreground">
            <div className="font-semibold text-foreground">Standort Wuppertal</div>
            <div className="mt-1">Kreuzstraße 20 · 42277 Wuppertal</div>
          </div>
          <nav className="md:col-span-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground/80 md:justify-end">
            <a
              href="https://schneider-bodenbeschichtungen.de/impressum/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground"
            >
              Impressum
            </a>
            <a
              href="https://schneider-bodenbeschichtungen.de/datenschutzerklaerung/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground"
            >
              Datenschutz
            </a>
            <a
              href="https://schneider-bodenbeschichtungen.de/agb/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground"
            >
              AGB
            </a>
          </nav>
        </div>
        <div className="mt-6 text-xs text-muted-foreground">
          © {new Date().getFullYear()} Schneider Bodenbeschichtungen GmbH
        </div>
      </div>
    </footer>
  );
}

/* ─── Page ─── */
function LandingV2() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main>
        <Hero />
        <TrustBar />
        <AudienceFit />
        <PainPoints />
        <ProjectProof />
        <UseCases />
        <WhyAndAuthority />
        <Reviews />
        <FAQ />
        <ContactForm />
      </main>
      <Footer />
      <StickyMobileCTA />
    </div>
  );
}
